/**
 * Inbox Popup for Poker Reborn
 *
 * Modal overlay showing player's inbox messages. Each message has a
 * type-colored accent bar, title, body, timestamp, and CTA button.
 *
 * Follows the LeaveWarningPopup pattern:
 * - Extends Container directly (no base Popup class)
 * - Creates overlay, background panel, title, close button
 * - Self-destructs via destroy()
 * - Cleans up event listeners on destroy to prevent memory leaks
 *
 * @see docs/screens/lobby/popups/INBOX_POPUP.md - Visual specs, dimensions, behavior
 */

import { Container, Graphics, Text, Rectangle, FederatedPointerEvent, FederatedWheelEvent } from 'pixi.js';
import {
  Button,
  SECONDARY_BUTTON_STYLE,
  GOLD_BUTTON_STYLE,
  PRIMARY_BUTTON_STYLE,
} from '@/components/common/Button';
import { inboxService } from '@/services/InboxService';
import type { InboxMessage, InboxCTA, InboxState } from '@/services/InboxService';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },
  popup: {
    width: 420,
    height: 520,
    widthMobile: 340,
    heightMobile: 460,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
    padding: 16,
  },
  title: {
    fill: 0xffffff,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  closeButton: {
    size: 28,
    color: 0x888888,
    hoverColor: 0xffffff,
  },
  message: {
    padding: 12,
    gap: 10,
    titleFontSize: 16,
    bodyFontSize: 13,
    timeFontSize: 11,
    titleColor: 0xffffff,
    bodyColor: 0xaaaaaa,
    timeColor: 0x666666,
    backgroundColor: 0x12122a,
    borderRadius: 8,
    accentBarWidth: 3,
  },
  ctaButton: {
    width: 80,
    height: 30,
    fontSize: 13,
  },
  scrollbar: {
    width: 4,
    minThumbHeight: 20,
    trackColor: 0x222244,
    thumbColor: 0x555577,
    trackAlpha: 0.5,
    thumbAlpha: 0.8,
    cornerRadius: 2,
    rightInset: 2,
  },
  emptyState: {
    fill: 0x666666,
    fontSize: 16,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  breakpoints: {
    mobile: 480,
  },
};

/** Default accent style (info) */
const DEFAULT_MESSAGE_STYLE = { accentColor: 0x78909c };

/** Type-specific accent colors */
const MESSAGE_TYPE_STYLES: Record<string, { accentColor: number }> = {
  info: DEFAULT_MESSAGE_STYLE,
  action: { accentColor: 0xffd54f },
  reward: { accentColor: 0x66bb6a },
};

// =============================================================================
// INBOX POPUP
// =============================================================================

export class InboxPopup extends Container {
  private overlay: Graphics;
  private panel: Container;
  private panelBackground: Graphics;
  private messageListContainer: Container;
  private emptyText: Text;
  private closeBtn: Container;
  private scrollbar: Graphics;

  private popupWidth: number;
  private popupHeight: number;
  private screenWidth: number;
  private screenHeight: number;

  // Scroll state
  private listTop = 0;
  private listHeight = 0;
  private contentHeight = 0;
  private scrollY = 0;
  private isScrollDragging = false;
  private scrollDragStartY = 0;
  private scrollDragStartScrollY = 0;

  constructor(screenWidth: number, screenHeight: number) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    const isMobile = screenWidth < STYLES.breakpoints.mobile;
    this.popupWidth = isMobile ? STYLES.popup.widthMobile : STYLES.popup.width;
    this.popupHeight = isMobile ? STYLES.popup.heightMobile : STYLES.popup.height;

    // Full-screen semi-transparent overlay (tap to close)
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointertap', () => { this.close(); });
    this.addChild(this.overlay);

    // Centered panel container
    this.panel = new Container();
    this.panel.x = (screenWidth - this.popupWidth) / 2;
    this.panel.y = (screenHeight - this.popupHeight) / 2;
    this.addChild(this.panel);

    // Panel background
    this.panelBackground = new Graphics();
    this.panelBackground.roundRect(0, 0, this.popupWidth, this.popupHeight, STYLES.popup.cornerRadius);
    this.panelBackground.fill(STYLES.popup.fill);
    this.panelBackground.setStrokeStyle({ width: 2, color: STYLES.popup.stroke });
    this.panelBackground.stroke();
    this.panelBackground.eventMode = 'static';
    this.panelBackground.on('pointerdown', (e) => { e.stopPropagation(); });
    this.panel.addChild(this.panelBackground);

    // Title text
    const titleText = new Text({
      text: 'Inbox',
      style: STYLES.title,
    });
    titleText.x = STYLES.popup.padding;
    titleText.y = STYLES.popup.padding;
    this.panel.addChild(titleText);

    // Close button (X)
    this.closeBtn = this.createCloseButton();
    this.closeBtn.x = this.popupWidth - STYLES.popup.padding - STYLES.closeButton.size;
    this.closeBtn.y = STYLES.popup.padding;
    this.panel.addChild(this.closeBtn);

    // Scrollable message list area
    this.listTop = STYLES.popup.padding + 40;
    const listBottom = this.popupHeight - STYLES.popup.padding;
    this.listHeight = listBottom - this.listTop;
    const contentWidth = this.popupWidth - STYLES.popup.padding * 2;

    this.messageListContainer = new Container();
    this.messageListContainer.x = STYLES.popup.padding;
    this.messageListContainer.y = this.listTop;
    this.panel.addChild(this.messageListContainer);

    // Create a mask for scrollable area
    const listMask = new Graphics();
    listMask.rect(
      STYLES.popup.padding,
      this.listTop,
      contentWidth,
      this.listHeight,
    );
    listMask.fill(0xffffff);
    this.panel.addChild(listMask);
    this.messageListContainer.mask = listMask;

    // Scroll interaction
    this.messageListContainer.eventMode = 'static';
    this.messageListContainer.hitArea = new Rectangle(0, 0, contentWidth, this.listHeight);
    this.messageListContainer.on('pointerdown', this.onScrollPointerDown, this);
    this.messageListContainer.on('pointermove', this.onScrollPointerMove, this);
    this.messageListContainer.on('pointerup', this.onScrollPointerUp, this);
    this.messageListContainer.on('pointerupoutside', this.onScrollPointerUp, this);
    this.messageListContainer.on('wheel', this.onScrollWheel, this);

    // Scrollbar (drawn on panel, outside mask so it's always visible)
    this.scrollbar = new Graphics();
    this.scrollbar.x = this.popupWidth - STYLES.popup.padding - STYLES.scrollbar.rightInset;
    this.scrollbar.y = this.listTop;
    this.scrollbar.visible = false;
    this.scrollbar.eventMode = 'none';
    this.panel.addChild(this.scrollbar);

    // Empty state text
    this.emptyText = new Text({
      text: 'No messages',
      style: STYLES.emptyState,
    });
    this.emptyText.anchor.set(0.5, 0.5);
    this.emptyText.x = (this.popupWidth - STYLES.popup.padding * 2) / 2;
    this.emptyText.y = this.listHeight / 2;
    this.emptyText.visible = false;
    this.messageListContainer.addChild(this.emptyText);

    // Render current state
    this.refresh();

    // Listen for live updates while popup is open
    inboxService.on('inboxUpdated', this.onInboxUpdated);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Resize overlay and re-center panel on window resize */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Resize overlay
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Re-center panel
    this.panel.x = (width - this.popupWidth) / 2;
    this.panel.y = (height - this.popupHeight) / 2;
  }

  // ===========================================================================
  // LIFECYCLE
  // ===========================================================================

  private close(): void {
    this.destroy();
  }

  override destroy(): void {
    // Remove event listener to prevent memory leaks
    inboxService.off('inboxUpdated', this.onInboxUpdated);
    super.destroy({ children: true });
  }

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  private onInboxUpdated = (_state: InboxState): void => {
    this.refresh();
  };

  private onCTAClick(cta: InboxCTA, messageId: string): void {
    inboxService.executeCTA(cta, messageId);
  }

  // ===========================================================================
  // SCROLL HANDLERS
  // ===========================================================================

  private onScrollPointerDown(event: FederatedPointerEvent): void {
    this.isScrollDragging = true;
    const local = this.panel.toLocal(event.global);
    this.scrollDragStartY = local.y;
    this.scrollDragStartScrollY = this.scrollY;
  }

  private onScrollPointerMove(event: FederatedPointerEvent): void {
    if (!this.isScrollDragging) return;

    const local = this.panel.toLocal(event.global);
    const deltaY = local.y - this.scrollDragStartY;
    const maxScroll = this.contentHeight - this.listHeight;

    if (maxScroll <= 0) return; // Everything fits, no scrolling needed

    // Clamp scroll: 0 = top, -maxScroll = bottom
    this.scrollY = Math.max(-maxScroll, Math.min(0, this.scrollDragStartScrollY + deltaY));
    this.messageListContainer.y = this.listTop + this.scrollY;
    this.drawScrollbar();
  }

  private onScrollPointerUp(): void {
    this.isScrollDragging = false;
  }

  private onScrollWheel(event: FederatedWheelEvent): void {
    const maxScroll = this.contentHeight - this.listHeight;
    if (maxScroll <= 0) return;

    const deltaY = event.deltaY;

    this.scrollY = Math.max(-maxScroll, Math.min(0, this.scrollY - deltaY));
    this.messageListContainer.y = this.listTop + this.scrollY;
    this.drawScrollbar();
  }

  // ===========================================================================
  // RENDERING
  // ===========================================================================

  private refresh(): void {
    const state = inboxService.getLastState();
    const messages = state?.messages ?? [];

    // Clear previous message cards (keep emptyText)
    const children = [...this.messageListContainer.children];
    for (const child of children) {
      if (child !== this.emptyText && child !== this.messageListContainer.mask) {
        child.destroy({ children: true });
      }
    }

    // Reset scroll position
    this.scrollY = 0;
    this.messageListContainer.y = this.listTop;

    if (messages.length === 0) {
      this.emptyText.visible = true;
      this.contentHeight = 0;
      return;
    }

    this.emptyText.visible = false;

    const contentWidth = this.popupWidth - STYLES.popup.padding * 2;
    let yOffset = 0;

    for (const message of messages) {
      const card = this.renderMessage(message, contentWidth);
      card.y = yOffset;
      this.messageListContainer.addChild(card);
      yOffset += card.height + STYLES.message.gap;
    }

    // Store total content height (remove trailing gap)
    this.contentHeight = yOffset > 0 ? yOffset - STYLES.message.gap : 0;

    // Expand hitArea to cover full content so scrolled-down cards receive events
    const interactiveHeight = Math.max(this.listHeight, this.contentHeight);
    this.messageListContainer.hitArea = new Rectangle(0, 0, contentWidth, interactiveHeight);

    this.drawScrollbar();
  }

  private renderMessage(message: InboxMessage, contentWidth: number): Container {
    const card = new Container();
    const typeStyle = MESSAGE_TYPE_STYLES[message.type] ?? DEFAULT_MESSAGE_STYLE;

    // Card background
    const cardPadding = STYLES.message.padding;
    let yPos = cardPadding;
    const xPos = cardPadding + STYLES.message.accentBarWidth + 4;
    const textMaxWidth = contentWidth - xPos - STYLES.ctaButton.width - cardPadding - 8;

    // Title
    const titleText = new Text({
      text: message.title,
      style: {
        fill: STYLES.message.titleColor,
        fontSize: STYLES.message.titleFontSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    titleText.x = xPos;
    titleText.y = yPos;
    // Truncate title if too wide
    if (titleText.width > textMaxWidth) {
      titleText.style.wordWrap = true;
      titleText.style.wordWrapWidth = textMaxWidth;
    }
    card.addChild(titleText);

    // CTA button (right-aligned, same row as title)
    const ctaButtonStyle = this.getCTAButtonStyle(message);
    const ctaButton = new Button({
      label: message.cta.label,
      width: STYLES.ctaButton.width,
      height: STYLES.ctaButton.height,
      style: {
        ...ctaButtonStyle,
        fontSize: STYLES.ctaButton.fontSize,
      },
    });
    ctaButton.x = contentWidth - cardPadding - STYLES.ctaButton.width;
    ctaButton.y = yPos;
    ctaButton.onClick = () => { this.onCTAClick(message.cta, message.id); };
    card.addChild(ctaButton);

    yPos += titleText.height + 4;

    // Body text
    const bodyText = new Text({
      text: message.body,
      style: {
        fill: STYLES.message.bodyColor,
        fontSize: STYLES.message.bodyFontSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        wordWrap: true,
        wordWrapWidth: contentWidth - xPos - cardPadding,
      },
    });
    bodyText.x = xPos;
    bodyText.y = yPos;
    card.addChild(bodyText);

    yPos += bodyText.height + 4;

    // Timestamp
    const timeText = new Text({
      text: formatRelativeTime(message.created_at),
      style: {
        fill: STYLES.message.timeColor,
        fontSize: STYLES.message.timeFontSize,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    timeText.x = xPos;
    timeText.y = yPos;
    card.addChild(timeText);

    yPos += timeText.height + cardPadding;

    // Card background (drawn after measuring content)
    const cardBackground = new Graphics();
    cardBackground.roundRect(0, 0, contentWidth, yPos, STYLES.message.borderRadius);
    cardBackground.fill(STYLES.message.backgroundColor);
    card.addChildAt(cardBackground, 0);

    // Left accent bar
    const accentBar = new Graphics();
    accentBar.roundRect(0, 0, STYLES.message.accentBarWidth, yPos, 2);
    accentBar.fill(typeStyle.accentColor);
    card.addChild(accentBar);

    return card;
  }

  private getCTAButtonStyle(message: InboxMessage): Partial<typeof SECONDARY_BUTTON_STYLE> {
    if (!message.cta.command) {
      // No action - use secondary (blue) for info/ack
      if (message.type === 'reward') return PRIMARY_BUTTON_STYLE;
      return SECONDARY_BUTTON_STYLE;
    }
    // Action CTA - use gold
    return GOLD_BUTTON_STYLE;
  }

  // ===========================================================================
  // UI HELPERS
  // ===========================================================================

  private drawScrollbar(): void {
    this.scrollbar.clear();

    // Hide if content fits within visible area
    if (this.contentHeight <= this.listHeight) {
      this.scrollbar.visible = false;
      return;
    }

    this.scrollbar.visible = true;

    const { width, minThumbHeight, trackColor, thumbColor, trackAlpha, thumbAlpha, cornerRadius } =
      STYLES.scrollbar;

    // Track (full height of list area)
    this.scrollbar.roundRect(0, 0, width, this.listHeight, cornerRadius);
    this.scrollbar.fill({ color: trackColor, alpha: trackAlpha });

    // Thumb height proportional to visible / total ratio
    const thumbHeight = Math.max(
      minThumbHeight,
      (this.listHeight / this.contentHeight) * this.listHeight,
    );

    // Thumb position based on scroll offset
    const maxScroll = this.contentHeight - this.listHeight;
    const scrollRatio = maxScroll > 0 ? -this.scrollY / maxScroll : 0;
    const thumbY = scrollRatio * (this.listHeight - thumbHeight);

    this.scrollbar.roundRect(0, thumbY, width, thumbHeight, cornerRadius);
    this.scrollbar.fill({ color: thumbColor, alpha: thumbAlpha });
  }

  private createCloseButton(): Container {
    const container = new Container();
    const size = STYLES.closeButton.size;

    const background = new Graphics();
    background.roundRect(0, 0, size, size, 4);
    background.fill({ color: 0x000000, alpha: 0 });
    container.addChild(background);

    // Draw X
    const xGraphics = new Graphics();
    xGraphics.setStrokeStyle({ width: 2, color: STYLES.closeButton.color });
    xGraphics.moveTo(6, 6);
    xGraphics.lineTo(size - 6, size - 6);
    xGraphics.moveTo(size - 6, 6);
    xGraphics.lineTo(6, size - 6);
    xGraphics.stroke();
    container.addChild(xGraphics);

    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', () => { this.close(); });

    return container;
  }
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Format an ISO 8601 timestamp as a relative time string.
 * Returns "Just now", "Xm ago", "Xh ago", "Yesterday", "Xd ago", or date.
 */
function formatRelativeTime(isoTimestamp: string): string {
  if (!isoTimestamp) return '';

  const date = new Date(isoTimestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;

  // Older than 30 days: show date
  return date.toLocaleDateString();
}
